/**
 * Session labels derived from a session's MOST-RECENT prompt.
 *
 * AI_SESSION has no human-readable name field (the proto's `latest_summary`
 * metric was dropped — see its reserved comment), so a session otherwise
 * renders as a meaningless notification_id: the full UUID in dim entries, an
 * 8-char prefix in chips. A session IS one conversation, so its latest prompt
 * is a faithful, human-readable label.
 *
 * This module resolves each session id to the summary
 * (DIMENSION_FIELD_PROMPT_ENRICHED_PROMPT, the `prompt_summary` column) of its
 * most-recent prompt — the highest DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP for
 * that notification_id that carries a non-empty summary (LITE / scaffolding
 * frames have none). This is the same "latest non-empty prompt_summary" the
 * api-server's per-session `latest_prompt_summary` argMax uses (migration
 * 20260514000002).
 *
 * Returns null for a session with no usable summary in the window; callers
 * fall back to the short id prefix the rest of the app uses — no session ever
 * renders unlabeled.
 *
 * NEVER throws to callers: a batch query failure resolves those ids to null
 * (caller fallback) and leaves them uncached so a later call retries.
 */

import { getAuthContextEpoch, onAuthContextChange } from "./authTokenStore.js";
import { readLatestSessionRows } from "./sessionPreviewBatch.js";

// Keep each request bounded below the analytics IN-list ceiling. Normal display
// pages fit in one batch.
const BATCH_SIZE = 100;
// Row budget per session for readLatestSessionRows' second read.
const ROWS_PER_SESSION = 8;
// A label is a NAME, not a paragraph — collapse whitespace and clip the (1–2
// sentence) summary so it fits chips / column rows. The full text still lives
// behind the row's OverflowTooltip via CSS truncation.
const MAX_LABEL_CHARS = 120;

const F_PROMPT_NOTIFICATION_ID = "DIMENSION_FIELD_PROMPT_NOTIFICATION_ID";
const F_EVENT_TIMESTAMP = "DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP";
const F_ENRICHED = "DIMENSION_FIELD_PROMPT_ENRICHED_PROMPT";
const F_EVENT_ID = "DIMENSION_FIELD_PROMPT_EVENT_ID";

// "<windowKey>::<id>" → { label, traceId }. The window is part of the key
// because the label is the most-recent prompt WITHIN it: change the timeline
// and a session's latest in-window prompt (hence its label) can change, so a
// new window must recompute rather than serve a stale label.
let _cache = new Map();

// Bound the cache so a long-lived page browsing many sessions across many
// windows can't grow it without limit. Map preserves insertion order, so
// deleting the first key is a cheap FIFO eviction (good enough — labels are
// re-derivable and cheap; an exact LRU isn't worth the bookkeeping).
const CACHE_MAX = 2000;
function cacheSet(key, value) {
    if (_cache.size >= CACHE_MAX && !_cache.has(key)) {
        _cache.delete(_cache.keys().next().value);
    }
    _cache.set(key, value);
}

export function resetSessionLabels() {
    _cache = new Map();
}

onAuthContextChange(resetSessionLabels);

// Stable cache-key fragment for a window. Distinct windows → distinct keys.
function windowKey(timeRange) {
    return timeRange ? JSON.stringify(timeRange) : "default";
}

// Collapse a multi-line summary to a single trimmed line and clip to a
// label-sized length. Returns null for an empty/whitespace summary so the
// caller falls through to the id prefix.
export function summaryToLabel(summary) {
    const s = String(summary ?? "").replace(/\s+/g, " ").trim();
    if (!s) return null;
    return s.length > MAX_LABEL_CHARS ? `${s.slice(0, MAX_LABEL_CHARS - 1).trimEnd()}…` : s;
}

function previewFromRows(rows) {
    rows.sort((a, b) => {
        const byTime = String(b.event_time ?? "").localeCompare(String(a.event_time ?? ""));
        if (byTime) return byTime;
        return String(b.event_id ?? "").localeCompare(String(a.event_id ?? ""));
    });
    const fallbackTraceId = rows.find((r) => r.event_id)?.event_id;
    for (const row of rows) {
        const label = summaryToLabel(row.enriched);
        if (label) {
            return {
                label,
                traceId: row.event_id ? String(row.event_id) : (fallbackTraceId ? String(fallbackTraceId) : null),
            };
        }
    }
    return {
        label: null,
        traceId: fallbackTraceId ? String(fallbackTraceId) : null,
    };
}

// Each session's most-recent prompt, and its most-recent prompt that carries a
// summary (see readLatestSessionRows). A session with no prompt in the window
// is an authoritative miss; one whose rows the second read cut off is left out
// so it stays uncached. Throws on query failure so the caller can leave the
// whole batch uncached for retry.
//
// Empty summaries are excluded by the metric predicate, which also drops NULL
// summaries; a whitespace-only summary still wins and yields no label. We do NOT
// filter `message_type = 'SCAFFOLDING'`: in agent sessions the tool-loop turns
// ARE scaffolding yet carry the descriptive summaries ("Inspecting Docker
// images…"), so excluding them buries the label under the one terse user turn.
async function fetchPromptPreviewBatch(runQuery, rowsToObjects, ids, timeRange) {
    const P = (field) => ({ stepAlias: "p", field });
    const { rowsById, missing } = await readLatestSessionRows({
        runQuery: async (spec) => rowsToObjects(await runQuery(spec)),
        ids,
        timeRange,
        notificationField: F_PROMPT_NOTIFICATION_ID,
        timestampField: F_EVENT_TIMESTAMP,
        summaryField: F_ENRICHED,
        dimensions: [
            { field: P(F_PROMPT_NOTIFICATION_ID), outputAlias: "session_id" },
            { field: P(F_EVENT_TIMESTAMP), outputAlias: "event_time" },
            { field: P(F_ENRICHED), outputAlias: "enriched" },
            { field: P(F_EVENT_ID), outputAlias: "event_id" },
        ],
        rowsPerSession: ROWS_PER_SESSION,
    });
    const out = new Map(missing.map((id) => [id, { label: null, traceId: null }]));
    for (const [id, sessionRows] of rowsById) out.set(id, previewFromRows(sessionRows));
    return out;
}

/**
 * Resolve the most-recent usable prompt and trace id for a set of session ids,
 * scoped to a time window so both point at the same timeline entry.
 *
 * @param {Array<string>} ids session notification_ids
 * @param {object} timeRange proto time_range (absolute start/end) scoping the
 *        "most recent prompt" — the current timeline. Both call sites derive it
 *        from the viewed range, so the label matches the data on screen.
 * @returns {Promise<Map<string, {label: string|null, traceId: string|null}>>}
 */
export async function getSessionPromptPreviews(ids, timeRange) {
    const authContextEpoch = getAuthContextEpoch();
    const out = new Map();
    const want = [...new Set((ids ?? []).map(String))].filter(Boolean);
    if (!want.length) return out;

    const wk = windowKey(timeRange);
    const uncached = [];
    for (const id of want) {
        const key = `${authContextEpoch}::${wk}::${id}`;
        if (_cache.has(key)) out.set(id, _cache.get(key));
        else uncached.push(id);
    }
    if (!uncached.length) return out;

    // Never throws to callers (the "never throws" contract): the dynamic
    // import below is the one site outside the per-batch try/catch, so wrap
    // the whole resolution — on any failure every uncached id resolves to null
    // (→ caller falls back to the id prefix) and stays uncached for retry.
    try {
        // analyticsClient is imported dynamically to keep this module out of
        // any static init-order cycle (dimensionDefs + apiClient import it, and
        // analyticsClient pulls those in transitively).
        const { runQuery, rowsToObjects } = await import("./analyticsClient.js");
        const batches = [];
        for (let start = 0; start < uncached.length; start += BATCH_SIZE) {
            batches.push(uncached.slice(start, start + BATCH_SIZE));
        }
        await Promise.all(batches.map(async (batch) => {
            try {
                const previews = await fetchPromptPreviewBatch(runQuery, rowsToObjects, batch, timeRange);
                if (getAuthContextEpoch() !== authContextEpoch) return;
                for (const id of batch) {
                    const preview = previews.get(id);
                    if (preview) cacheSet(`${authContextEpoch}::${wk}::${id}`, preview);
                    out.set(id, preview ?? { label: null, traceId: null });
                }
            } catch (err) {
                console.warn(`[sessionLabels] label batch fetch failed for ${batch.length} sessions; falling back to id prefixes:`, err);
                for (const id of batch) out.set(id, { label: null, traceId: null });
            }
        }));
    } catch (err) {
        console.warn("[sessionLabels] resolution failed; falling back to id prefix:", err);
        for (const id of uncached) out.set(id, { label: null, traceId: null });
    }
    if (getAuthContextEpoch() !== authContextEpoch) {
        return new Map(want.map((id) => [id, { label: null, traceId: null }]));
    }
    return out;
}

/**
 * Resolve most-recent-prompt-summary labels for a set of session ids.
 *
 * @returns {Promise<Map<string, string|null>>} id → label (null = no usable
 *          summary; caller falls back to the short id prefix)
 */
export async function getSessionLabels(ids, timeRange) {
    const previews = await getSessionPromptPreviews(ids, timeRange);
    return new Map([...previews].map(([id, preview]) => [id, preview.label]));
}
